import { useEffect, useState } from "react";
import { ApiException } from "../../../../../services/api/ApiException";
import { toast } from "react-toastify";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import styles from "./styles.module.scss";
import { IArea } from "../../../../../@types/area.type";
import {
  addOrUpdateArea,
  deleteArea,
} from "../../../../../services/api/areas/AreasService";
import { getAllResources } from "../../../../../services/api/resource/resourceService";
import { IKeyValue } from "../../../../../@types/key-value.type";
import { useConfirm } from "../../../../../hooks/useConfirm";
import vectorIcon from "../../../../../assets/_VectorIcon_.svg";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

type FormData = {
  id?: string;
  areaName: string;
  description?: string;
  resourceId: string;
};

const schema = z.object({
  id: z.string().optional(),
  areaName: z
    .string()
    .min(1, "Nome da Área é obrigatório")
    .max(100, "Nome da Área deve ter no máximo 100 caracteres"),
  description: z.string().optional(),
  resourceId: z.string().min(1, "Responsável é obrigatório"),
});

interface FormAreasProps {
  pageType: "create" | "update" | "seeMore";
  areaData?: IArea;
  isSyncActive?: boolean;
}

export const FormAreas: React.FC<FormAreasProps> = ({ pageType, areaData, isSyncActive = false }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [options, setOptions] = useState<IKeyValue[]>([]);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      id: areaData?.id,
      areaName: areaData?.areaName || "",
      description: areaData?.description || "",
      resourceId: areaData?.resourceId || "",
    },
  });

  const [openModalConfirm, setOpenModalConfirm] = useState(false);
  const handleOpenModalConfirm = () => setOpenModalConfirm(true);
  const handleCloseModalConfirm = () => setOpenModalConfirm(false);

  const { showConfirm, Confirm } = useConfirm(
    "Tem certeza que deseja excluir a área?",
    openModalConfirm,
    handleCloseModalConfirm
  );

  const loadResources = async () => {
    try {
      const response = (await getAllResources(false)) as IKeyValue[];
      setOptions(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    loadResources();
  }, []);

  const navigate = useNavigate();

  const onSubmit = async (data: FormData) => {
    try {
      setLoading(true);
      await addOrUpdateArea(data);
      reset();
      toast.success("Área salva com sucesso!");
      navigate("/configurations/areas");
    } catch (error: any) {
      toast.error(areaData ? "Erro ao alterar a Área!" : "Erro ao criar Área");
      new ApiException(error.message || "Erro ao processar a requisição.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    handleOpenModalConfirm();
    const isConfirm = await showConfirm();

    if (isConfirm) {
      await deleteArea(areaData?.id as string);
      toast.success("Área excluída com sucesso!");
      navigate("/configurations/areas");
    }
  };

  return (
    <>
      <div className={styles.back}>
        <Button
          className={styles.button}
          onClick={() => navigate("/configurations/areas")}
        >
          {vectorIcon && <img src={vectorIcon} />}
          Voltar a tela anterior
        </Button>
      </div>
      <div className={styles.title}>
        <h1>{pageType === "update" ? `${areaData?.areaName}` : "Nova Área"}</h1>
      </div>
      <div></div>

      <form onSubmit={handleSubmit(onSubmit)} className={styles.formArea}>
        <div className={styles.fields}>
          <Controller
            name="areaName"
            control={control}
            render={({ field }) => (
              <TextField
                label="Nome da Área"
                placeholder={
                  pageType === "create" ? "Insira o nome da área aqui..." : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.areaName}
                helperText={errors.areaName?.message}
                fullWidth
                disabled={isSyncActive || pageType === "update"}
              />
            )}
          />
          <Controller
            name="resourceId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <Autocomplete
                disabled={isSyncActive}
                options={options}
                getOptionLabel={(option) => option.value || ""}
                value={
                  options.find((option) => option.key === field.value) || null
                }
                onChange={(_, newValue) =>
                  field.onChange(newValue ? newValue.key : "")
                }
                noOptionsText="Sem opções"
                renderOption={(props, option) => {
                  return (
                    <li {...props} key={option.key}>
                      {option.value}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Responsável"
                    placeholder={
                      pageType === "create" ? "Selecione o responsável" : ""
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    error={!!errors.resourceId}
                    helperText={errors.resourceId?.message}
                    fullWidth
                  />
                )}
              />
            )}
          />
        </div>
        <div className={styles.fields}>
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                label="Descrição"
                placeholder={
                  pageType === "create"
                    ? "Insira a descrição da área aqui..."
                    : ""
                }
                InputLabelProps={{
                  shrink: true,
                }}
                value={field.value}
                onChange={field.onChange}
                error={!!errors.description}
                helperText={errors.description?.message}
                fullWidth
                multiline
                disabled={isSyncActive}
              />
            )}
          />
        </div>
        <div className={styles.btnContainer}>
          {areaData?.id && (
            <Button
              variant="contained"
              color="secondary"
              type="button"
              onClick={handleDeleteClick}
              className={styles.btnsDeleted}
              disabled={loading || isSyncActive}
            >
              Excluir Área
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            type="submit"
            className={styles.btnsSave}
            disabled={loading || isSyncActive}
          >
            Salvar
          </Button>
        </div>
      </form>
      {Confirm}
    </>
  );
};
